import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { useHistory, useLocation } from 'react-router-dom'
import block from 'bem-cn'

import { ROUTE_URLS } from 'router/routes'

import { ReactComponent as Arrow } from 'shared/img/arrow-left.svg'
import { ReactComponent as Close } from 'shared/img/close-icon.svg'

import { usePoll } from 'shared/hooks/usePoll'
import { AvailablePrmotionTabs, IGetUserPromotionsFilters } from 'shared/models/BonusSystem'
import { BonusPromotionStatus, IUserPromotion } from 'shared/models/Promotion'
import { mediaSize } from 'shared/style/var'

import { getUserPromotions } from 'features/bonusSystem/actions'
import { selectBonusPromotionsComm, selectBonusPromotionsData } from 'features/bonusSystem/selectors'
import { selectLocaleDict } from 'features/locale/selectors'

import Pagination from 'components/Pagination/Pagination'

import BonusItem from './BonusItem/BonusItem'
import NoData from './NoData/NoData'

import './BonusPromotions.scss'

type Filter = IGetUserPromotionsFilters & {
  activeTab: AvailablePrmotionTabs
}

const b = block('bonus-promotions')

export const currentStatusToArrayOfStatuses: Record<AvailablePrmotionTabs, BonusPromotionStatus[]> = {
  [BonusPromotionStatus.active]: [
    BonusPromotionStatus.active,
    // BonusPromotionStatus.available,
    // BonusPromotionStatus.available_completed,
    BonusPromotionStatus.completed,
  ],
  [BonusPromotionStatus.available]: [BonusPromotionStatus.available, BonusPromotionStatus.available_completed],
  [BonusPromotionStatus.completed]: [BonusPromotionStatus.wait, BonusPromotionStatus.paid],
  [BonusPromotionStatus.expired]: [BonusPromotionStatus.expired],
}

type PromotionTabKey = 'activePromotions' | 'available' | 'completed' | 'expired'

const promotionTabs: { textId: PromotionTabKey; value: AvailablePrmotionTabs }[] = [
  {
    textId: 'activePromotions',
    value: BonusPromotionStatus.active,
  },
  {
    textId: 'available',
    value: BonusPromotionStatus.available,
  },
  {
    textId: 'completed',
    value: BonusPromotionStatus.completed,
  },
  {
    textId: 'expired',
    value: BonusPromotionStatus.expired,
  },
]

const BonusPromotions: React.FC = () => {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()

  const locale = useSelector(selectLocaleDict, shallowEqual)
  const promotionsData = useSelector(selectBonusPromotionsData, shallowEqual)
  const { isLoading } = useSelector(selectBonusPromotionsComm, shallowEqual)

  const { count, promos } = promotionsData

  const isMobileOrTablet = useMediaQuery({ maxWidth: mediaSize.tablet })

  const [filters, setFilters] = useState<Filter>({
    page: 1,
    page_size: isMobileOrTablet ? 20 : 10,
    status: currentStatusToArrayOfStatuses[BonusPromotionStatus.active],
    activeTab: BonusPromotionStatus.active,
  })

  const queryParams = new URLSearchParams(location.search)
  const statusParam = (queryParams.get('status') || '') as AvailablePrmotionTabs
  const allowedStatuses: AvailablePrmotionTabs[] = [
    BonusPromotionStatus.active,
    BonusPromotionStatus.available,
    BonusPromotionStatus.completed,
    BonusPromotionStatus.expired,
  ]
  const queryStatus = allowedStatuses.includes(statusParam) ? statusParam : undefined

  const payloadFilters = useMemo(() => {
    const effectiveTab = queryStatus ?? filters.activeTab
    const { activeTab: _activeTab, ...rest } = filters
    const tabJustChanged = queryStatus && queryStatus !== filters.activeTab
    return {
      ...rest,
      status: currentStatusToArrayOfStatuses[effectiveTab],
      ...(tabJustChanged ? { page: 1 } : {}),
    }
  }, [queryStatus, filters])

  const handleTabChange = useCallback(
    (tab: AvailablePrmotionTabs) => {
      const base = ROUTE_URLS.MAIN()
      const searchParams = new URLSearchParams(location.search)
      searchParams.set('openedModals', '/promotions')
      searchParams.set('status', tab)
      history.push(`${base}?${searchParams.toString()}`)
    },
    [history, location.search],
  )

  const handlePageChange = useCallback((page: number) => {
    setFilters(prevFilters => ({ ...prevFilters, page }))
  }, [])

  useEffect(() => {
    dispatch(getUserPromotions(payloadFilters))
  }, [dispatch, payloadFilters])

  usePoll(
    async () => {
      dispatch(getUserPromotions(payloadFilters))
    },
    { delay: 15000 },
  )

  useEffect(() => {
    if (queryStatus) {
      setFilters({
        page: 1,
        page_size: isMobileOrTablet ? 20 : 10,
        status: currentStatusToArrayOfStatuses[queryStatus],
        activeTab: queryStatus,
      })
    }
  }, [queryStatus, isMobileOrTablet])

  const renderTabButton = useCallback(
    (tab: { textId: PromotionTabKey; value: AvailablePrmotionTabs }) => (
      <button
        key={tab.value}
        type="button"
        className={b('tab', { active: (queryStatus || filters.activeTab) === tab.value })}
        onClick={() => handleTabChange(tab.value)}>
        {locale.profile[tab.textId]}
      </button>
    ),
    [locale.profile, queryStatus, filters.activeTab, handleTabChange],
  )

  const renderPromotionsList = useCallback(() => {
    if (isLoading) {
      return <div className={b('loading')}>Loading...</div>
    }

    if (!promos?.length) {
      return <NoData />
    }

    return (
      <>
        <div className={b('items')}>
          {promos.map((promotion: IUserPromotion, index: number) => (
            <BonusItem key={`${promotion.id}-${index}`} promotion={promotion} filters={filters} />
          ))}
        </div>
        {count > (filters.page_size || 10) && (
          <Pagination
            activePage={filters.page || 1}
            total={count}
            limit={filters.page_size || 10}
            onClick={handlePageChange}
          />
        )}
      </>
    )
  }, [isLoading, promos, count, filters.page, filters.page_size, handlePageChange])

  return (
    <div className={b('wrapper')}>
      <div className={b('header')}>
        <Arrow
          className={b('back-icon')}
          onClick={() => history.push(`${ROUTE_URLS.MAIN()}?openedModals=%2Fprofile%2Ftab`)}
        />
        <div className={b('title')}>{locale.profile.promotions}</div>
        <div className={b('close-block')} onClick={() => history.push(ROUTE_URLS.MAIN())}>
          <Close />
        </div>
      </div>
      <div className={b('content-container')}>
        <div className={b('tabs')}>{promotionTabs.map(renderTabButton)}</div>
        <div className={b('content')}>{renderPromotionsList()}</div>
      </div>
    </div>
  )
}

export { BonusPromotions }
export default BonusPromotions
